import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Building2 } from 'lucide-react'
import type { Account, Property } from '@/types/entities'
import {
  SectionCard, EmptyState, DataTable, ListToolbar, Badge, HealthBadge,
  type Column, type ToolbarView,
} from '@/components/ui'
import { money, num, pct } from '@/lib/format'
import type { AccountDerived } from '../lib'
import { latestRevenue, latestOcc, locStr } from '../lib'

const VIEWS: ToolbarView[] = [
  { id: 'all', label: 'All' },
  { id: 'owned', label: 'Owned' },
  { id: 'managed', label: 'Managed' },
]

export default function PropertiesTab({ account, d }: { account: Account; d: AccountDerived }) {
  const navigate = useNavigate()
  const [view, setView] = useState('all')
  const [search, setSearch] = useState('')

  let rows = d.properties
  if (view === 'owned') rows = rows.filter((p) => p.relationshipType === 'Owned')
  if (view === 'managed') rows = rows.filter((p) => p.relationshipType === 'Managed-3PM')
  if (search.trim()) {
    const q = search.toLowerCase()
    rows = rows.filter((p) =>
      p.name.toLowerCase().includes(q) ||
      locStr(p).toLowerCase().includes(q) ||
      (p.externalIds?.lcode ?? '').toLowerCase().includes(q))
  }

  const columns: Column<Property>[] = [
    {
      key: 'name', header: 'Property', sortValue: (p) => p.name,
      render: (p) => (
        <div className="aw-cell-stack">
          <span className="aw-link">{p.name}</span>
          <span className="aw-cell-sub">{locStr(p)}</span>
        </div>
      ),
    },
    {
      key: 'lcode', header: 'Lcode', width: 78, sortValue: (p) => p.externalIds?.lcode ?? '',
      render: (p) => <span className="aw-mono">{p.externalIds?.lcode ?? '—'}</span>,
    },
    {
      key: 'rel', header: 'Relationship', width: 130, sortValue: (p) => p.relationshipType,
      render: (p) => <Badge tone={p.relationshipType === 'Owned' ? 'navy' : 'info'} variant="outline">{p.relationshipType}</Badge>,
    },
    {
      key: 'nrsf', header: 'NRSF', align: 'right', width: 100, sortValue: (p) => p.nrsf ?? -1,
      render: (p) => <span className="num">{num(p.nrsf)}</span>,
    },
    {
      key: 'units', header: 'Units', align: 'right', width: 80, sortValue: (p) => p.totalUnits ?? -1,
      render: (p) => <span className="num">{num(p.totalUnits)}</span>,
    },
    {
      key: 'occ', header: 'Occupancy', align: 'right', width: 100, sortValue: (p) => latestOcc(p) ?? -1,
      render: (p) => <span className="num">{pct(latestOcc(p))}</span>,
    },
    {
      key: 'rev', header: 'In-Place Rev', align: 'right', width: 130, sortValue: (p) => latestRevenue(p) ?? -1,
      render: (p) => <span className="num">{money(latestRevenue(p))}</span>,
    },
    {
      key: 'health', header: 'Status', align: 'right', width: 120,
      render: (p) => p.health ? <HealthBadge health={p.health} /> : <Badge tone="neutral" variant="soft">{p.stabilization ?? p.state}</Badge>,
    },
  ]

  if (!d.properties.length) {
    return (
      <SectionCard pad>
        <EmptyState icon={<Building2 size={36} />} title="No properties on this account" hint={`No owned or managed properties are associated with ${account.name}.`} />
      </SectionCard>
    )
  }

  return (
    <SectionCard pad={false}>
      <ListToolbar
        views={VIEWS}
        activeView={view}
        onView={setView}
        search={search}
        onSearch={setSearch}
        searchPlaceholder="Search properties, cities, Lcodes…"
        count={rows.length}
        onAdd={() => { /* no-op in prototype */ }}
        addLabel="Add Property"
      />
      <DataTable<Property>
        columns={columns}
        rows={rows}
        getRowKey={(p) => p.id}
        onRowClick={(p) => navigate(`/properties/${p.id}`)}
        initialSort={{ key: 'rev', dir: 'desc' }}
        dense
        emptyText="No properties match this filter."
      />
    </SectionCard>
  )
}
